import { useEffect, useState } from 'react';
import { showToast } from '@/components/base/Toast';
import { submitLoan, submitHtxLoan, getSeasonsByFarmer } from '@/store';
import { listFinancingProducts, type BackendFinancingProduct } from '@/api/lending';
import { ApiError } from '@/api/client';
import type { Farmer } from '@/types';

const AMOUNT_PRESETS = [10_000_000, 20_000_000, 30_000_000, 50_000_000];

interface Props {
  htxId?: string;
  farmers: Farmer[];
  onClose: () => void;
  onCreated: () => void;
}

/**
 * HTX creates a cash advance for either one of its own farmers, or the
 * cooperative itself ("tạo trực tiếp cho hợp tác xã hoặc tạo cho nông hộ").
 *
 * Farmer mode: backend auto-approves (LoanService.submitApplication's
 * `isHtxSelfService` branch) as long as the amount is within the farmer's
 * available limit — the modal shows that outcome immediately rather than a
 * "chờ duyệt" state, since there is none. No contract picker here — a Loan
 * has no `contractId` at submission time in either the DTO or the backend
 * input (contracts attach to a loan afterward via the Agrida-only
 * `attach-contract` action), same as the farmer's own "Xin vay".
 *
 * Season IS collected in farmer mode, unlike an earlier version of this
 * modal — the farmer's own "Xin vay" wizard requires one (ApplyLoanSheet.tsx),
 * and a loan with none showed "Không gắn mùa vụ" with no way to reconcile it
 * against a contract or harvest later (Feedback 08 B8). Optional here (not
 * required) since an HTX may create an advance before the farmer has an
 * active season set up.
 *
 * HTX mode: the cooperative itself is the borrower — no farmer/season to
 * pick, and this is deliberately NEVER auto-approved (new financial exposure
 * the existing risk calibration doesn't cover) — it always goes through the
 * standard tiered approval queue, which the modal states plainly instead of
 * implying instant approval.
 */
export function CreateLoanForFarmerModal({ htxId, farmers, onClose, onCreated }: Props) {
  const [borrowerType, setBorrowerType] = useState<'FARMER' | 'HTX'>('FARMER');
  const [farmerId, setFarmerId] = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [products, setProducts] = useState<BackendFinancingProduct[]>([]);
  const [financingProductId, setFinancingProductId] = useState('');
  const [amount, setAmount] = useState<number | null>(null);
  const [purpose, setPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const seasons = farmerId
    ? getSeasonsByFarmer(farmerId).filter((s) => s.status === 'Đang canh tác' || s.status === 'Chuẩn bị' || s.status === 'Sắp thu hoạch')
    : [];

  useEffect(() => {
    listFinancingProducts()
      .then((rows) => {
        setProducts(rows);
        if (rows.length > 0) setFinancingProductId(rows[0]._id);
      })
      .catch(() => undefined);
  }, []);

  async function submit() {
    if (borrowerType === 'FARMER' && !farmerId) return showToast('Chọn nông hộ', 'error');
    if (!financingProductId) return showToast('Chọn sản phẩm vay', 'error');
    if (!amount) return showToast('Nhập số tiền', 'error');
    setSubmitting(true);
    try {
      if (borrowerType === 'HTX') {
        if (!htxId) return showToast('Không xác định được hợp tác xã', 'error');
        await submitHtxLoan({
          htxId,
          loai: 'Tiền',
          financingProductId,
          soTien: amount,
          mucDich: purpose || undefined,
        });
        showToast('Đã gửi yêu cầu vay cho HTX — đang chờ duyệt', 'success');
      } else {
        await submitLoan({
          farmerId,
          htxId,
          loai: 'Tiền',
          financingProductId,
          seasonId: seasonId || undefined,
          soTien: amount,
          mucDich: purpose || undefined,
        });
        showToast('Đã tạo và duyệt khoản tạm ứng cho nông hộ', 'success');
      }
      onCreated();
    } catch (e) {
      showToast(e instanceof ApiError ? e.message : 'Tạo khoản vay thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    // z-[97]: matches BottomSheet's convention — must outrank BottomNav (z-50)
    // and the two navigation menus (SlideMenu z-[90], AvatarMenu z-[95]/[96]),
    // or the bottom tab bar renders on top of this sheet's own submit button.
    <div className="fixed inset-0 z-[97] flex items-end sm:items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-white p-4 space-y-3 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-900">Tạo khoản vay</h3>
          <button type="button" onClick={onClose} className="text-gray-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setBorrowerType('FARMER')}
            className={`rounded-lg border px-3 py-2 text-xs font-semibold ${borrowerType === 'FARMER' ? 'border-agrida-600 bg-agrida-600 text-white' : 'border-gray-300 text-gray-700'}`}
          >
            Cho nông hộ vay
          </button>
          <button
            type="button"
            onClick={() => setBorrowerType('HTX')}
            className={`rounded-lg border px-3 py-2 text-xs font-semibold ${borrowerType === 'HTX' ? 'border-agrida-600 bg-agrida-600 text-white' : 'border-gray-300 text-gray-700'}`}
          >
            HTX vay trực tiếp
          </button>
        </div>

        {borrowerType === 'FARMER' ? (
          <p className="rounded-lg bg-agrida-50 border border-agrida-100 px-3 py-2 text-[11px] text-agrida-800">
            Khoản vay do HTX tạo cho nông hộ của mình được duyệt ngay nếu trong hạn mức còn lại — không cần chờ phòng tín dụng.
          </p>
        ) : (
          <p className="rounded-lg bg-sky-50 border border-sky-100 px-3 py-2 text-[11px] text-sky-800">
            Khoản vay HTX đứng tên trực tiếp luôn cần qua quy trình duyệt tiêu chuẩn — không được duyệt ngay như khoản vay hộ nông hộ.
          </p>
        )}

        {borrowerType === 'FARMER' && (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Nông hộ</label>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={farmerId} onChange={(e) => { setFarmerId(e.target.value); setSeasonId(''); }}>
                <option value="">— Chọn nông hộ —</option>
                {farmers.map((f) => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Mùa vụ / thửa đất</label>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} disabled={!farmerId}>
                <option value="">— Không gắn mùa vụ —</option>
                {seasons.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Sản phẩm vay</label>
          <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" value={financingProductId} onChange={(e) => setFinancingProductId(e.target.value)}>
            {products.map((p) => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Số tiền đề nghị</label>
          <div className="flex flex-wrap gap-2">
            {AMOUNT_PRESETS.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setAmount(v)}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${amount === v ? 'border-agrida-600 bg-agrida-600 text-white' : 'border-gray-300 text-gray-700'}`}
              >
                {(v / 1_000_000).toFixed(0)} triệu
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Mục đích</label>
          <input
            type="text"
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="VD: Mua vật tư vụ Đông Xuân"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>

        <button
          type="button"
          disabled={submitting}
          onClick={submit}
          className="w-full rounded-lg bg-agrida-600 text-white text-sm font-semibold py-2.5 disabled:opacity-50"
        >
          {submitting ? 'Đang tạo...' : borrowerType === 'HTX' ? 'Gửi yêu cầu vay' : 'Tạo & duyệt khoản vay'}
        </button>
      </div>
    </div>
  );
}
